import { appLogger } from '@/utils/app-logger/appLogger';
import { createConsoleLogger } from '@/utils/devtools/console-logger';

import {
    type CmpConsentBridgeEvent,
    getCmpConsentBridgeState,
    resetCmpConsentBridgeStateForTests,
} from './cmpConsentBridgeState';

/** Poll interval while waiting for async `window.cbsoptanon` (Optanon / Ketch bridge). */
export const CMP_BRIDGE_POLL_MS = 100;

/** Stop polling after this duration if the CMP never appears. */
export const CMP_BRIDGE_POLL_TIMEOUT_MS = 15_000;

export type { CmpConsentBridgeEvent };

export type CmpConsentConsumer = 'tracking' | 'footer' | 'stitcher';

const { log: logCmpConsent } = createConsoleLogger('cmp-consent');

/**
 * Dev-only trace when a CMP bridge consumer handles an event.
 * Enable via the `tn_logger` cookie (e.g. `cmp-consent`).
 */
export function logCmpConsentConsumer(
    consumer: CmpConsentConsumer,
    event: string,
    data?: unknown,
): void {
    if (data === undefined) {
        logCmpConsent(`[${consumer}] ${event}`);
        return;
    }
    logCmpConsent(`[${consumer}] ${event}`, data);
}

type CmpConsentBridgeHandler = (cmp: CbsOptanonCmp) => void;

function clearCmpBridgePolling(): void {
    const state = getCmpConsentBridgeState();
    if (state.pollId !== undefined) {
        window.clearInterval(state.pollId);
        state.pollId = undefined;
    }
    if (state.pollTimeoutId !== undefined) {
        window.clearTimeout(state.pollTimeoutId);
        state.pollTimeoutId = undefined;
    }
}

function invokeHandlerSafely(handler: CmpConsentBridgeHandler, cmp: CbsOptanonCmp): void {
    try {
        handler(cmp);
    } catch (error) {
        // One subscriber must not break others or page load.
        appLogger.error(
            'CMP consent bridge subscriber failed',
            { component: 'cmpConsentBridge' },
            error,
        );
    }
}

function emitCmpConsentEvent(event: CmpConsentBridgeEvent, cmp: CbsOptanonCmp): void {
    const state = getCmpConsentBridgeState();
    state.firedEvents.add(event);
    state.cachedCmp = cmp;
    for (const handler of state.subscribers[event]) {
        invokeHandlerSafely(handler, cmp);
    }
}

function registerCmpBridgeListeners(): boolean {
    const state = getCmpConsentBridgeState();
    if (state.cmpRegistered || typeof window === 'undefined' || !window.cbsoptanon) {
        return state.cmpRegistered;
    }

    if (!state.scriptsReadyRegistered) {
        try {
            window.cbsoptanon.onScriptsReady((cmp) => {
                emitCmpConsentEvent('scriptsReady', cmp);
                try {
                    cmp.ot?.awaitInitialConsent?.(() => {
                        // Optanon calls back again ("interaction") after its timeout callback;
                        // the explicit choice already arrives via consentChanged.
                        if (getCmpConsentBridgeState().firedEvents.has('initialConsent')) {
                            return;
                        }
                        emitCmpConsentEvent('initialConsent', cmp);
                    });
                } catch (error) {
                    appLogger.error(
                        'CMP awaitInitialConsent registration failed',
                        { component: 'cmpConsentBridge' },
                        error,
                    );
                }
            });
            state.scriptsReadyRegistered = true;
        } catch (error) {
            appLogger.error(
                'CMP bridge onScriptsReady registration failed',
                { component: 'cmpConsentBridge' },
                error,
            );
            return false;
        }
    }

    if (!state.consentChangedRegistered) {
        try {
            window.cbsoptanon.cmd?.push((cmp) => {
                try {
                    cmp.ot?.addOnConsentChangedHandler?.(() => {
                        emitCmpConsentEvent('consentChanged', cmp);
                    });
                } catch (error) {
                    appLogger.error(
                        'CMP consentChanged handler registration failed',
                        { component: 'cmpConsentBridge' },
                        error,
                    );
                }
            });
            state.consentChangedRegistered = true;
        } catch (error) {
            appLogger.error(
                'CMP bridge consentChanged registration failed',
                { component: 'cmpConsentBridge' },
                error,
            );
            return false;
        }
    }

    state.cmpRegistered = true;
    clearCmpBridgePolling();
    return true;
}

/**
 * Polls for `window.cbsoptanon` when registration has not succeeded yet.
 * Each call resets the poll deadline so late-mounted consumers get a fresh window.
 */
function ensureCmpBridgePolling(): void {
    if (typeof window === 'undefined') {
        return;
    }

    if (registerCmpBridgeListeners()) {
        return;
    }

    const state = getCmpConsentBridgeState();

    if (state.pollId === undefined) {
        state.pollId = window.setInterval(() => {
            if (registerCmpBridgeListeners()) {
                clearCmpBridgePolling();
            }
        }, CMP_BRIDGE_POLL_MS);
    }

    if (state.pollTimeoutId !== undefined) {
        window.clearTimeout(state.pollTimeoutId);
    }
    state.pollTimeoutId = window.setTimeout(() => {
        clearCmpBridgePolling();
    }, CMP_BRIDGE_POLL_TIMEOUT_MS);
}

/**
 * Registers Ketch/Optanon consent listeners once per page session and fans out
 * `scriptsReady`, `initialConsent`, and `consentChanged` to subscribers.
 *
 * Client-side only — CMP script tags are injected SSR via `initCbsCmpScriptsServer`.
 * Safe to call when CMP is absent: polls briefly then stops silently.
 *
 * State lives on `globalThis` so federated MFE remotes share one pub/sub registry.
 */
export function initCmpConsentBridge(): void {
    if (typeof window === 'undefined') {
        return;
    }

    const state = getCmpConsentBridgeState();
    if (state.cmpBridgeInitialized) {
        return;
    }
    state.cmpBridgeInitialized = true;

    ensureCmpBridgePolling();
}

/** Returns the last `cmp` instance passed to bridge events, if any. */
export function getCachedCmp(): CbsOptanonCmp | undefined {
    return getCmpConsentBridgeState().cachedCmp;
}

/**
 * Subscribes to a CMP bridge event. Replays immediately when the event already fired
 * (supports late-mounted MFEs such as video SmartTag).
 */
export function subscribeCmpConsent(
    event: CmpConsentBridgeEvent,
    handler: CmpConsentBridgeHandler,
): () => void {
    const state = getCmpConsentBridgeState();
    state.subscribers[event].add(handler);

    if (!state.cmpRegistered && !state.firedEvents.has('initialConsent')) {
        ensureCmpBridgePolling();
    }

    if (state.firedEvents.has(event) && state.cachedCmp) {
        invokeHandlerSafely(handler, state.cachedCmp);
    }

    return () => {
        state.subscribers[event].delete(handler);
    };
}

/** Test-only reset — not for production use. */
export function resetCmpConsentBridgeForTests(): void {
    clearCmpBridgePolling();
    resetCmpConsentBridgeStateForTests();
}
